import { useCallback, useEffect, useRef, useState } from "react";

export type SaveStatus = "idle" | "saving" | "saved";

/**
 * Agrupa ediciones seguidas en una sola llamada al backend y expone en qué va
 * el guardado. Cada `schedule` reinicia el temporizador; al desmontar se envía
 * lo que quedó pendiente.
 */
export function useDebouncedSave<T>(
  save: (value: T) => Promise<unknown>,
  delay = 1500,
) {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ value: T } | null>(null);
  const alive = useRef(true);
  const saveRef = useRef(save);

  useEffect(() => {
    saveRef.current = save;
  });

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const next = pending.current;
    pending.current = null;
    if (!next) return;
    saveRef.current(next.value).then(
      () => {
        if (!alive.current) return;
        setStatus("saved");
        savedTimer.current = setTimeout(() => {
          if (alive.current) setStatus("idle");
        }, 2000);
      },
      // El caller ya avisa del error con su toast; acá solo se apaga el estado.
      () => {
        if (alive.current) setStatus("idle");
      },
    );
  }, []);

  const schedule = useCallback(
    (value: T) => {
      pending.current = { value };
      setStatus("saving");
      if (savedTimer.current) clearTimeout(savedTimer.current);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);
    },
    [delay, flush],
  );

  // Cambiar de línea de negocio o salir de la página no debe perder lo pendiente.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      flush();
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, [flush]);

  return { schedule, status };
}
